import { useCallback, useRef } from "react";

export const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

/**
 * How long to wait for a reCAPTCHA token before giving up and sending the
 * request without one.
 *
 * A successful `executeAsync()` normally settles in a few hundred milliseconds,
 * so this only comes into play when the widget cannot run at all. It has to be
 * short enough that a user does not think the button is broken, and long enough
 * not to skip the token on a slow connection.
 */
const TOKEN_TIMEOUT_MS = 4000;

/**
 * Invisible reCAPTCHA for the OTP screens.
 *
 * Sending an OTP is the only paid action on the unauthenticated surface — every
 * call bills us for an SMS — and it was the one with no bot-cost control in
 * front of it. DAO's email/password sign-in has verified a captcha since it was
 * built; the Retail Circle phone flow never has.
 *
 * Shared by the "Send OTP" form and the "Resend code" link so the two cannot
 * drift apart, and so the missing-key guard below exists in exactly one place.
 *
 * The token rides along as `recaptcha`, which is the field name the backend's
 * VerifyOtpCaptcha middleware reads (and the same one DAO's existing forms
 * already send).
 */
export default function useOtpCaptcha() {
  const recaptchaRef = useRef();

  const enabled = Boolean(RECAPTCHA_SITE_KEY);

  /**
   * Resolve a fresh token, or null when there is nothing to resolve.
   *
   * Never throws. A captcha is a cost control, not an authorisation check: if
   * the widget cannot produce a token — no site key configured, script blocked,
   * Google unreachable — the caller must still be able to attempt sign-in. The
   * backend decides what to do with a missing token (it logs rather than
   * refuses until RECAPTCHA_ENFORCE_RC_OTP is turned on), and the rate limits
   * underneath apply either way. Failing shut here would just mean an ad
   * blocker could lock a customer out of their own account.
   */
  const getToken = useCallback(async () => {
    if (!enabled || !recaptchaRef.current) {
      return null;
    }

    let timer;

    try {
      // Invisible reCAPTCHA refuses to issue a second token until it is reset,
      // so a retry (or a resend) would otherwise resolve to null forever.
      recaptchaRef.current.reset();

      // MUST be raced against a timeout. `executeAsync()` does NOT reject when
      // the widget cannot run — if the site key is not authorised for this
      // domain, or Google's script is blocked by an extension or a firewall, it
      // returns a promise that simply NEVER SETTLES. The try/catch below cannot
      // see that, so awaiting it bare hangs the caller forever: the sign-in
      // request is never sent and the button silently does nothing. Found on
      // localhost against the production key, which is domain-locked, but the
      // same hang hits any real user whose browser cannot reach Google.
      const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS);
      });

      const token = await Promise.race([
        recaptchaRef.current.executeAsync(),
        timeout,
      ]);

      if (!token) {
        // Deliberately a warning, not an error: sign-in is proceeding without a
        // token, which is the intended fail-open behaviour.
        console.warn(
          `[captcha] no reCAPTCHA token after ${TOKEN_TIMEOUT_MS}ms — continuing without one.`
        );
      }

      return token || null;
    } catch (error) {
      return null;
    } finally {
      clearTimeout(timer);
    }
  }, [enabled]);

  /** Merge the token into a request payload, omitting it when unavailable. */
  const withToken = useCallback(
    async (payload) => {
      const token = await getToken();

      return token ? { ...payload, recaptcha: token } : payload;
    },
    [getToken]
  );

  return { recaptchaRef, enabled, siteKey: RECAPTCHA_SITE_KEY, getToken, withToken };
}
